import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { createReportWorker } from '../worker/createWorker';
import { type PreviewSettings, type WorkerOutputMessage } from '../types';
import {
  isTablePrintingImprovementGroupExpansionEnabled,
  isTablePrintingImprovementForFiltersEnabled,
} from 'common/visualizations/helpers/VifSelectors';

type UsePDFWorkerProps = {
  onMessage: (message: MessageEvent<WorkerOutputMessage>) => void;
  onError?: (error: string) => void;
  settings?: PreviewSettings;
  rowCount?: number;
};

const ROW_COUNT_THRESHOLD_FOR_RESTARTING_WORKER = 5000;

export const usePdfWorker = (props: UsePDFWorkerProps) => {
  const { onMessage, onError, settings, rowCount } = props;
  const [worker, setWorker] = useState<Worker | null>(null);
  const newWorker = useRef<Worker | null>(null);
  const isInitializing = useRef(false);

  // Track configuration changes for large datasets to prevent memory issues.
  // These settings can cause memory leaks in the worker when changed without restarting.
  const pdfConfigurationState = useMemo(() => {
    const isLargeDataset = rowCount && rowCount > ROW_COUNT_THRESHOLD_FOR_RESTARTING_WORKER;
    const shouldEnableWorkerRestart =
      isTablePrintingImprovementGroupExpansionEnabled() || isTablePrintingImprovementForFiltersEnabled();
    if (!isLargeDataset || !shouldEnableWorkerRestart) {
      return null;
    }

    return JSON.stringify({
      pageSize: settings?.pageSize,
      orientation: settings?.orientation,
      pageBreak: settings?.pageBreak,
      columnWidth: settings?.columnWidth,
      textOverflow: settings?.textOverflow,
      fontSize: settings?.fontSize,
      groupExpansion: settings?.groupExpansion,
      reportingPeriod: settings?.reportingPeriod,
      showGridlines: settings?.showGridlines,
      metadataVisibility: settings?.metadataVisibility,
      showLogo: settings?.showLogo,
    });
  }, [
    settings?.pageSize,
    settings?.orientation,
    settings?.pageBreak,
    settings?.columnWidth,
    settings?.textOverflow,
    settings?.fontSize,
    settings?.groupExpansion,
    settings?.reportingPeriod,
    settings?.showGridlines,
    settings?.metadataVisibility,
    settings?.showLogo,
    rowCount,
  ]);

  const initWorker = useCallback(async () => {
    if (isInitializing.current) {
      return;
    }

    isInitializing.current = true;

    try {
      // Cleanup worker if in case there is one present
      if (newWorker.current) {
        newWorker.current.terminate();
        newWorker.current = null;
      }
      const { worker: createdWorker } = await createReportWorker();
      createdWorker.onmessage = onMessage;
      setWorker(createdWorker);
      newWorker.current = createdWorker; // Capture reference for cleanup
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Worker initialization failed');
    } finally {
      isInitializing.current = false;
    }
  }, [onError, onMessage]);

  useEffect(() => {
    initWorker();
    return () => {
      if (newWorker.current) {
        newWorker.current.terminate();
      }
    };
  }, [initWorker]);

  // Restart worker when configuration settings change for large datasets.
  // This prevents memory leaks that can occur when the worker processes
  // different configurations without being restarted.
  // Spike Ticket: https://socrata.atlassian.net/browse/EN-91790
  useEffect(() => {
    if (pdfConfigurationState && newWorker.current) {
      initWorker();
    }
  }, [pdfConfigurationState, initWorker]);

  return worker;
};
